/**
 * Interactive 7×24 day-parting grid for feature #74.
 *
 * Rendering rules (from CLAUDE.md brief):
 *   - 7 rows (Mon..Sun) × 24 cols (0..23)
 *   - on-cell: emerald tint, off-cell: zinc-100
 *   - 6–8px rounded corners, zinc borders
 *   - Inter font, light theme, emerald→sky gradient for headings
 *   - no emojis, no competitor names
 *
 * Interaction:
 *   - Click a cell to toggle on/off.
 *   - Click-drag across cells to bulk-apply the toggle direction chosen
 *     by the first cell in the drag (paint on if it was off, paint off
 *     if it was on).
 *   - Row/column/all shortcuts set contiguous selections in one shot.
 */

import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	GRID_COLS,
	GRID_ROWS,
	ROW_LABELS,
	cloneGrid,
	emptyGrid,
} from "./api";
import type { GridMatrix } from "./api";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DaypartingGridProps {
	value: GridMatrix;
	onChange: (next: GridMatrix) => void;
	disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatHour(h: number): string {
	const hh = h.toString().padStart(2, "0");
	return `${hh}:00`;
}

function countOnCells(grid: GridMatrix): number {
	let n = 0;
	for (const row of grid) for (const cell of row) if (cell) n++;
	return n;
}

// ---------------------------------------------------------------------------
// Cell — memoised to keep re-renders contained while dragging.
// ---------------------------------------------------------------------------

interface CellProps {
	on: boolean;
	row: number;
	col: number;
	disabled: boolean;
	onPointerDown: (row: number, col: number) => void;
	onPointerEnter: (row: number, col: number) => void;
}

const Cell = memo(function Cell({
	on,
	row,
	col,
	disabled,
	onPointerDown,
	onPointerEnter,
}: CellProps) {
	const base =
		"h-7 w-7 rounded-[6px] border transition-colors select-none touch-none";
	const active =
		"bg-emerald-400/80 border-emerald-500 hover:bg-emerald-500";
	const inactive = "bg-zinc-100 border-zinc-200 hover:bg-zinc-200";
	const lockedStyle = disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer";
	return (
		<button
			type="button"
			role="gridcell"
			aria-selected={on}
			aria-label={`${ROW_LABELS[row]} ${formatHour(col)} ${on ? "enabled" : "disabled"}`}
			data-row={row}
			data-col={col}
			tabIndex={-1}
			disabled={disabled}
			onPointerDown={(e) => {
				if (disabled) return;
				e.preventDefault();
				onPointerDown(row, col);
			}}
			onPointerEnter={(e) => {
				if (disabled) return;
				// buttons=1 → primary button down.
				if (e.buttons === 1) onPointerEnter(row, col);
			}}
			className={`${base} ${on ? active : inactive} ${lockedStyle}`}
		/>
	);
});

// ---------------------------------------------------------------------------
// DaypartingGrid
// ---------------------------------------------------------------------------

export function DaypartingGrid({
	value,
	onChange,
	disabled = false,
}: DaypartingGridProps) {
	const [dragging, setDragging] = useState(false);
	const dragPaintRef = useRef<boolean>(true); // value to paint while dragging
	const draftRef = useRef<GridMatrix>(value);

	// Keep draft ref fresh whenever external value changes.
	useEffect(() => {
		draftRef.current = cloneGrid(value);
	}, [value]);

	// Release drag globally — users may pointerup off-grid.
	useEffect(() => {
		if (!dragging) return;
		const release = () => setDragging(false);
		window.addEventListener("pointerup", release);
		window.addEventListener("pointercancel", release);
		return () => {
			window.removeEventListener("pointerup", release);
			window.removeEventListener("pointercancel", release);
		};
	}, [dragging]);

	const handlePointerDown = useCallback(
		(row: number, col: number) => {
			const current = draftRef.current[row]?.[col] ?? false;
			const paint = !current;
			dragPaintRef.current = paint;
			const next = cloneGrid(draftRef.current);
			next[row][col] = paint;
			draftRef.current = next;
			setDragging(true);
			onChange(next);
		},
		[onChange],
	);

	const handlePointerEnter = useCallback(
		(row: number, col: number) => {
			const paint = dragPaintRef.current;
			if (draftRef.current[row]?.[col] === paint) return;
			const next = cloneGrid(draftRef.current);
			next[row][col] = paint;
			draftRef.current = next;
			onChange(next);
		},
		[onChange],
	);

	const setAll = useCallback(
		(on: boolean) => {
			if (disabled) return;
			const next: GridMatrix = Array.from({ length: GRID_ROWS }, () =>
				Array.from({ length: GRID_COLS }, () => on),
			);
			draftRef.current = next;
			onChange(next);
		},
		[disabled, onChange],
	);

	const toggleRow = useCallback(
		(row: number) => {
			if (disabled) return;
			const allOn = draftRef.current[row].every(Boolean);
			const paint = !allOn;
			const next = cloneGrid(draftRef.current);
			for (let c = 0; c < GRID_COLS; c++) next[row][c] = paint;
			draftRef.current = next;
			onChange(next);
		},
		[disabled, onChange],
	);

	const toggleCol = useCallback(
		(col: number) => {
			if (disabled) return;
			const allOn = draftRef.current.every((r) => r[col]);
			const paint = !allOn;
			const next = cloneGrid(draftRef.current);
			for (let r = 0; r < GRID_ROWS; r++) next[r][col] = paint;
			draftRef.current = next;
			onChange(next);
		},
		[disabled, onChange],
	);

	const stats = useMemo(() => {
		const on = countOnCells(value);
		const total = GRID_ROWS * GRID_COLS;
		return { on, total };
	}, [value]);

	return (
		<div
			className="flex flex-col gap-4 rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm"
			onPointerLeave={() => setDragging(false)}
		>
			<div className="flex items-center justify-between gap-4">
				<div>
					<h3 className="text-sm font-semibold text-zinc-900">
						Active hours
					</h3>
					<p className="text-xs text-zinc-500">
						{stats.on} of {stats.total} hours enabled.
					</p>
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => setAll(true)}
						disabled={disabled}
						className="rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50"
					>
						Select all
					</button>
					<button
						type="button"
						onClick={() => setAll(false)}
						disabled={disabled}
						className="rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50"
					>
						Clear
					</button>
				</div>
			</div>

			<div
				role="grid"
				aria-label="Day-parting schedule grid, 7 days by 24 hours"
				className="w-full overflow-x-auto"
			>
				<div className="inline-flex flex-col gap-1">
					{/* Header row: hour columns */}
					<div className="flex items-center gap-1 pl-[3.25rem]">
						{Array.from({ length: GRID_COLS }).map((_, c) => (
							<button
								key={c}
								type="button"
								onClick={() => toggleCol(c)}
								disabled={disabled}
								className="h-6 w-7 rounded-[4px] text-[10px] font-mono text-zinc-500 transition-colors hover:bg-zinc-100 disabled:opacity-50"
								aria-label={`Toggle column ${formatHour(c)}`}
							>
								{c}
							</button>
						))}
					</div>

					{/* Grid body */}
					{Array.from({ length: GRID_ROWS }).map((_, r) => (
						<div key={r} className="flex items-center gap-1">
							<button
								type="button"
								onClick={() => toggleRow(r)}
								disabled={disabled}
								className="h-7 w-12 rounded-[6px] text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-100 disabled:opacity-50"
								aria-label={`Toggle row ${ROW_LABELS[r]}`}
							>
								{ROW_LABELS[r]}
							</button>
							{Array.from({ length: GRID_COLS }).map((_, c) => (
								<Cell
									key={c}
									row={r}
									col={c}
									on={value[r]?.[c] ?? false}
									disabled={disabled}
									onPointerDown={handlePointerDown}
									onPointerEnter={handlePointerEnter}
								/>
							))}
						</div>
					))}
				</div>
			</div>

			<div className="flex items-center justify-between text-xs text-zinc-500">
				<span>Click a cell to toggle. Click-drag to paint a range.</span>
				<span className="font-mono text-[11px] text-zinc-400">
					Mon 00:00 → Sun 23:00
				</span>
			</div>
		</div>
	);
}

// Re-export emptyGrid for callers that don't want to import from api.ts.
export { emptyGrid };
